// @ts-nocheck
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import axios from "axios";
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { URL } from "../../service/Service";
import AuthShell from "../../components/auth/AuthShell";
import { useTranslation } from "../../i18n/LanguageContext";

export default function ResetPasswordForm() {
  const { t } = useTranslation();
  const [userEmail, setUserEmail] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [userPasswordRepeat, setUserPasswordRepeat] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
  const navigate = useNavigate();
  const { token } = useParams();

  const passwordChecks = useMemo(
    () => ({
      length: userPassword.length >= 8,
      uppercase: /[A-Z]/.test(userPassword),
      lowercase: /[a-z]/.test(userPassword),
      number: /\d/.test(userPassword),
      symbol: /[^A-Za-z0-9]/.test(userPassword),
    }),
    [userPassword]
  );

  const passwordScore = Object.values(passwordChecks).filter(Boolean).length;
  const passwordStrength = userPassword ? (passwordScore / 5) * 100 : 0;
  const isPasswordValid = passwordScore === 5;

  const passwordStrengthClass =
    passwordScore <= 1
      ? "bg-red-500"
      : passwordScore <= 3
      ? "bg-amber-500"
      : passwordScore === 4
      ? "bg-yellow-500"
      : "bg-emerald-500";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!isPasswordValid) {
      setError(t("newPassword.policyError"));
      return;
    }

    if (userPassword !== userPasswordRepeat) {
      setError(t("newPassword.mismatch"));
      return;
    }

    setLoading(true);

    try {
      const response = await axios.patch(`${URL}/api/users/resetpassword`, {
        email: userEmail,
        newPassword: userPassword,
        token,
      });

      if (response.status === 200) {
        navigate("/login");
      } else {
        setError(t("newPassword.resetError"));
      }
    } catch (requestError) {
      setError(t("newPassword.resetError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow={t("newPassword.eyebrow")}
      title={t("newPassword.title")}
      description={t("newPassword.description")}
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className="ui-label">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={userEmail}
            onChange={(e) => {
              setUserEmail(e.target.value);
              setError("");
            }}
            className="ui-input"
            placeholder={t("reset.emailPlaceholder")}
          />
        </div>

        <div>
          <label htmlFor="password" className="ui-label">
            {t("newPassword.password")}
          </label>

          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              value={userPassword}
              onChange={(e) => {
                setUserPassword(e.target.value);
                setError("");
              }}
              className="ui-input pr-12"
              placeholder={t("newPassword.placeholder")}
              aria-describedby="password-requirements"
            />

            <button
              type="button"
              onClick={() => setShowPassword((visible) => !visible)}
              className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-gray-500 transition hover:bg-emerald-50 hover:text-green-cl focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-cl"
              aria-label={
                showPassword
                  ? t("login.hidePassword")
                  : t("login.showPassword")
              }
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>

          <p
            id="password-requirements"
            className="mt-2 text-xs leading-5 text-gray-500"
          >
            {t("newPassword.requirements")}
          </p>

          <div
            className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200"
            role="progressbar"
            aria-label={t("newPassword.strength")}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={passwordStrength}
          >
            <div
              className={`h-full rounded-full transition-all duration-300 ${passwordStrengthClass}`}
              style={{ width: `${passwordStrength}%` }}
            />
          </div>
        </div>

        <div>
          <label htmlFor="passwordRepeat" className="ui-label">
            {t("newPassword.repeat")}
          </label>

          <div className="relative">
            <input
              id="passwordRepeat"
              name="passwordRepeat"
              type={showPasswordRepeat ? "text" : "password"}
              autoComplete="new-password"
              required
              value={userPasswordRepeat}
              onChange={(e) => {
                setUserPasswordRepeat(e.target.value);
                setError("");
              }}
              className="ui-input pr-12"
              placeholder={t("newPassword.repeatPlaceholder")}
            />

            <button
              type="button"
              onClick={() =>
                setShowPasswordRepeat((visible) => !visible)
              }
              className="absolute inset-y-0 right-0 flex w-12 items-center justify-center rounded-r-xl text-gray-500 transition hover:bg-emerald-50 hover:text-green-cl focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-cl"
              aria-label={
                showPasswordRepeat
                  ? t("login.hidePassword")
                  : t("login.showPassword")
              }
            >
              {showPasswordRepeat ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="ui-alert-error" role="alert">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="ui-button-primary w-full"
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <svg
                className="mr-2 h-5 w-5 animate-spin text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t("newPassword.saving")}
            </div>
          ) : (
            t("newPassword.save")
          )}
        </button>
      </form>
    </AuthShell>
  );
}
